// radio-transcripts-db.js
//
// Persistencia durable (IndexedDB) de las transcripciones de radio (TETRA/MCX)
// ligadas al callId de cada llamada. El chat en pantalla (radioActivityLog del
// store map-v2) es efímero y se limpia al abrir una nueva llamada; esta capa
// conserva el historial completo por llamada, independiente del cap de 50
// entries en memoria.
//
// Diseño:
//   - DB 'kalan-radio', object store 'transcripts' keyPath 'id', índice 'callId'.
//   - Escrituras fire-and-forget: si IndexedDB no está disponible (modo privado,
//     navegador viejo, contexto sin storage) todo degrada a no-op silencioso —
//     la transcripción en vivo sigue funcionando, solo no se persiste.
//
// API:
//   saveEntry(entry)        → Promise<void>  (put; entry debe traer id)
//   getByCall(callId)       → Promise<Array> (ordenado por timestamp asc)
//   clearCall(callId)       → Promise<void>  (borra todas las entries del callId)

const DB_NAME = 'kalan-radio'
const DB_VERSION = 1
const STORE = 'transcripts'

let dbPromise = null

function hasIndexedDB() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null
}

function openDb() {
  if (dbPromise) return dbPromise
  if (!hasIndexedDB()) {
    dbPromise = Promise.reject(new Error('IndexedDB no disponible'))
    return dbPromise
  }
  dbPromise = new Promise(function(resolve, reject) {
    var req
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch (e) {
      reject(e)
      return
    }
    req.onupgradeneeded = function(ev) {
      var db = ev.target.result
      if (!db.objectStoreNames.contains(STORE)) {
        var store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('callId', 'callId', { unique: false })
      }
    }
    req.onsuccess = function() { resolve(req.result) }
    req.onerror = function() { reject(req.error || new Error('open error')) }
  })
  return dbPromise
}

// Solo guardamos campos serializables (structured clone). El entry del store
// ya es plano; copiamos para no arrastrar reactividad de Vue ni getters.
function serialize(entry) {
  var ts = entry.timestamp
  if (ts instanceof Date) ts = ts.getTime()
  return {
    id: entry.id,
    callId: entry.callId != null ? String(entry.callId) : '__none__',
    direction: entry.direction || null,
    channelId: entry.channelId != null ? entry.channelId : null,
    channelName: entry.channelName || null,
    callerId: entry.callerId || null,
    callerName: entry.callerName || null,
    durationMs: entry.durationMs || 0,
    timestamp: ts || 0,
    transcript: entry.transcript || null,
  }
}

export function saveEntry(entry) {
  if (!entry || !entry.id) return Promise.resolve()
  return openDb().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(serialize(entry))
      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() } // fire-and-forget: no propagar
      tx.onabort = function() { resolve() }
    })
  }).catch(function() { /* IndexedDB no disponible: no-op */ })
}

export function getByCall(callId) {
  var key = callId != null ? String(callId) : '__none__'
  return openDb().then(function(db) {
    return new Promise(function(resolve) {
      var out = []
      var idx = db.transaction(STORE, 'readonly').objectStore(STORE).index('callId')
      var req = idx.openCursor(IDBKeyRange.only(key))
      req.onsuccess = function() {
        var cur = req.result
        if (cur) { out.push(cur.value); cur.continue() }
        else {
          out.sort(function(a, b) { return (a.timestamp || 0) - (b.timestamp || 0) })
          resolve(out)
        }
      }
      req.onerror = function() { resolve(out) }
    })
  }).catch(function() { return [] })
}

export function clearCall(callId) {
  var key = callId != null ? String(callId) : '__none__'
  return openDb().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE, 'readwrite')
      var idx = tx.objectStore(STORE).index('callId')
      var req = idx.openCursor(IDBKeyRange.only(key))
      req.onsuccess = function() {
        var cur = req.result
        if (cur) { cur.delete(); cur.continue() }
      }
      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() }
      tx.onabort = function() { resolve() }
    })
  }).catch(function() { /* no-op */ })
}

export default { saveEntry, getByCall, clearCall }
